import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

// Predefined security questions (local, not from DB)
const SECURITY_QUESTIONS = [
  { id: 1, question: 'Name of your elementary school English teacher' },
  { id: 2, question: 'First country you visited' },
  { id: 3, question: 'What is your favorite movie of all time' }
];

const TwoFactorAuthModal = ({ isOpen, onClose, onSave }) => {
  const [authType, setAuthType] = useState('security-questions');
  const [setupStep, setSetupStep] = useState('select'); // select, setup, verify, success
  const [answers, setAnswers] = useState({
    question1: '',
    question2: '',
    question3: ''
  });
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({
    question1: '',
    question2: '',
    question3: '',
    otp: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
    // Clear error when user starts typing
    if (errors[questionKey]) {
      setErrors(prev => ({
        ...prev,
        [questionKey]: ''
      }));
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value.replace(/\D/g, '').slice(0, 6)); // Only digits, max 6
    if (errors.otp) {
      setErrors(prev => ({
        ...prev,
        otp: ''
      }));
    }
  };

  const handleAuthTypeChange = (type) => {
    setAuthType(type);
    setSetupStep('select');
    setOtp('');
    setAnswers({ question1: '', question2: '', question3: '' });
    setErrors({ question1: '', question2: '', question3: '', otp: '' });
  };

  const validateSecurityQuestions = () => {
    const newErrors = {
      question1: '',
      question2: '',
      question3: ''
    };

    if (!answers.question1.trim()) {
      newErrors.question1 = 'Please enter your answer to the security question.';
    }
    if (!answers.question2.trim()) {
      newErrors.question2 = 'Please enter your answer to the security question.';
    }
    if (!answers.question3.trim()) {
      newErrors.question3 = 'Please enter your answer to the security question.';
    }

    if (newErrors.question1 || newErrors.question2 || newErrors.question3) {
      setErrors(newErrors);
      return false;
    }
    return true;
  };

  const validateOtp = () => {
    if (!otp || otp.length !== 6) {
      setErrors(prev => ({
        ...prev,
        otp: 'Please enter a valid 6-digit code'
      }));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (authType === 'security-questions') {
      if (!validateSecurityQuestions()) {
        return;
      }

      setIsLoading(true);
      try {
        // Use the predefined security questions
        onSave({
          authType,
          method: 'security_questions',
          security_questions: [
            {
              question: SECURITY_QUESTIONS[0].question,
              answer: answers.question1
            },
            {
              question: SECURITY_QUESTIONS[1].question,
              answer: answers.question2
            },
            {
              question: SECURITY_QUESTIONS[2].question,
              answer: answers.question3
            }
          ]
        });
        onClose();
      } catch (error) {
        console.error('Error saving security questions:', error);
        toast.error('Failed to save security questions');
      } finally {
        setIsLoading(false);
      }
    } else if (authType === 'email' || authType === 'sms') {
      // For email and SMS, directly save without OTP verification
      setIsLoading(true);
      try {
        onSave({
          authType,
          method: authType
        });
        onClose();
      } catch (error) {
        console.error('Error saving 2FA method:', error);
        toast.error('Failed to save 2FA method');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    // Reset form
    setAuthType('security-questions');
    setSetupStep('select');
    setAnswers({
      question1: '',
      question2: '',
      question3: ''
    });
    setOtp('');
    setErrors({
      question1: '',
      question2: '',
      question3: '',
      otp: ''
    });
    setResendCountdown(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] animate-[fadeIn_0.3s_ease-in-out]"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg p-0 w-[90%] max-w-[600px] max-h-[90vh] overflow-y-auto relative shadow-[0_4px_20px_rgba(0,0,0,0.15)] animate-[slideDown_0.4s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b py-3 px-4 border-[#f4f4f4] flex justify-between items-center relative">
          <h4 className="text-[#000] m-0 font-normal leading-[1.42857143] font-['Bahnschrift_Light'] relative top-0 p-[5px]">
            2FA Authentication Setting
          </h4>
          <button
            className="float-right text-[21px] font-bold leading-none text-black p-0 cursor-pointer bg-transparent border-2 border-black rounded-full w-8 h-8 flex items-center justify-center -mt-[2px] transition-all duration-200 hover:border-opacity-50"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="p-[30px]">
          <div className="mb-6">
            <h3 className="text-base text-[#666] m-[-7px_-4px_-5px_16px] font-medium font-['Bahnschrift_Light']">
              Authentication Types
            </h3>
            <div className="flex gap-[30px] items-center md:flex-row flex-col md:items-center items-start md:gap-[30px] gap-[15px]">
              <label className="inline-block max-w-full mb-[5px] font-bold cursor-pointer text-[14px] text-[#666]">
                <input
                  type="radio"
                  name="authType"
                  value="email"
                  checked={authType === 'email'}
                  onChange={(e) => handleAuthTypeChange(e.target.value)}
                  className="w-[18px] h-[14px] m-[10px_2px] box-border p-0 cursor-pointer bg-[#d1d3d1] accent-[#ffd200] relative top-[2px]"
                />
                <span className="ml-2 text-[14px] text-[#666]">Email</span>
              </label>
              <label className="inline-block max-w-full mb-[5px] font-bold cursor-pointer text-[14px] text-[#666]">
                <input
                  type="radio"
                  name="authType"
                  value="sms"
                  checked={authType === 'sms'}
                  onChange={(e) => handleAuthTypeChange(e.target.value)}
                  className="w-[18px] h-[14px] m-[10px_2px] box-border p-0 cursor-pointer bg-[#d1d3d1] accent-[#ffd200] relative top-[2px]"
                />
                <span className="ml-2 text-[14px] text-[#666]">SMS</span>
              </label>
              <label className="inline-block max-w-full mb-[5px] font-bold cursor-pointer text-[14px] text-[#666]">
                <input
                  type="radio"
                  name="authType"
                  value="security-questions"
                  checked={authType === 'security-questions'}
                  onChange={(e) => handleAuthTypeChange(e.target.value)}
                  className="w-[18px] h-[14px] m-[10px_2px] box-border p-0 cursor-pointer bg-[#d1d3d1] accent-[#ffd200] relative top-[2px]"
                />
                <span className="ml-2 text-[14px] text-[#666]">Security Questions</span>
              </label>
            </div>
          </div>

          {/* Security Questions Section */}
          {authType === 'security-questions' && (
            <div className="space-y-4 mb-6">
              <>
                {SECURITY_QUESTIONS.map((q, idx) => (
                  <div key={idx} className="flex flex-col">
                    <label className="inline-block max-w-full mb-[5px] font-bold text-[14px] text-[#666]">
                      {idx + 1} : {q.question}
                    </label>
                    <input
                      type="text"
                      className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[14px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-white transition-[border-color] duration-200 focus:outline-none focus:border-[#ffd200] placeholder:text-[#bbb]"
                      placeholder="Answer"
                      value={answers[`question${idx + 1}`] || ''}
                      onChange={(e) => handleAnswerChange(`question${idx + 1}`, e.target.value)}
                    />
                    {errors[`question${idx + 1}`] && (
                      <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors[`question${idx + 1}`]}</p>
                    )}
                  </div>
                ))}
              </>
            </div>
          )}

          <div className="text-right border-t border-[#f4f4f4]">
            <button
              className="float-left text-black h-9 w-24 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-[#e0e0e0] text-[#666] border border-transparent rounded shadow-none hover:bg-[#d0d0d0] transition-all duration-200"
              onClick={handleClose}
            >
              Close
            </button>
            <button
              className="text-black h-9 w-24 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded shadow-none ml-[5px] hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Save'}
            </button>
          </div>
        </div>

        <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      </div>
    </div>
  );
};

export default TwoFactorAuthModal;
